import { BalanceData, MarketJson, MarketState, ShopJson, ShopState, Tribute, TributeJson, Wallet, WalletJson } from "@highmarch/domain";
import { HasSections, registerSaveSection } from "./saveSections";

/**
 * The offline economy's state (ADR 0013) and its save sections: `wallet` (gems, Marks, the ledger, the granted store
 * transactions), `shop` (today's crates and packs, the last loss), `market` (today's Trading Post and Black Market
 * buys) and `tribute` (when the fill started). The sections hold the domain objects' own JSON; each object restores
 * leniently, field by field, so one damaged line never costs a wallet of bought gems.
 */

/** A section's raw JSON when it is an object; its domain `fromJson` validates the fields. */
function asObject<T>(v: unknown): T | undefined {
  return typeof v === "object" && v !== null && !Array.isArray(v) ? (v as T) : undefined;
}

export const walletSection = registerSaveSection<WalletJson>("wallet", asObject, (v) => v);
export const shopSection = registerSaveSection<ShopJson>("shop", asObject, (v) => v);
export const marketSection = registerSaveSection<MarketJson>("market", asObject, (v) => v);
export const tributeSection = registerSaveSection<TributeJson>("tribute", asObject, (v) => v);

export class EconomyState {
  constructor(readonly wallet: Wallet, readonly shop: ShopState, readonly market: MarketState, readonly tribute: Tribute) {}

  static fresh(balance: BalanceData): EconomyState {
    return new EconomyState(new Wallet(balance.commerce.ledgerSize), new ShopState(), new MarketState(), new Tribute(balance));
  }

  static fromSave(balance: BalanceData, save: HasSections): EconomyState {
    return new EconomyState(
      Wallet.fromJson(balance.commerce.ledgerSize, walletSection.read(save)),
      ShopState.fromJson(shopSection.read(save)),
      MarketState.fromJson(marketSection.read(save)),
      Tribute.fromJson(balance, tributeSection.read(save)),
    );
  }

  applyTo(save: HasSections): void {
    walletSection.write(save, this.wallet.toJson());
    shopSection.write(save, this.shop.toJson());
    marketSection.write(save, this.market.toJson());
    tributeSection.write(save, this.tribute.toJson());
  }
}
